/**
 * Ambassador Dashboard
 * Ambassador payout home: Stripe Connect onboarding, commission balance and
 * ledger, referred creators, and directly assigned classes.
 *
 * Mirrors the venue dashboard but reads from /api/ambassador/*
 * (payee_type='ambassador'). Accessible to the 'ambassador' role (and admins).
 */
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { DollarSign, Users, BookOpen } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import { authedFetch, authedJson } from '@/lib/http';
import { formatDateCompact } from '@/lib/dateUtils';
import { PublicShell } from '@/components/public-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ConnectStatus {
  connected: boolean;
  onboardingStatus: string;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
}

interface Earnings {
  balanceCents: number;
  lifetimeEarnedCents: number;
  entries: Array<{
    id: string;
    entryType: string;
    amountCents: number;
    orderId: string | null;
    referenceNote: string | null;
    createdAt: string;
  }>;
}

interface CommissionShape {
  commissionMode: string | null;
  commissionPct: string | null;
  commissionFixedCents: number | null;
}

interface Referral extends CommissionShape {
  id: string;
  creatorId: string;
  creatorDisplayName: string | null;
  status: string;
  startsAt: string;
  endsAt: string | null;
}

interface AssignedClass extends CommissionShape {
  id: string;
  title: string;
}

interface ReferralsResponse {
  referrals: Referral[];
  assignedClasses: AssignedClass[];
}

function formatCommission(c: CommissionShape): string {
  if (c.commissionMode === 'fixed') {
    return `$${((c.commissionFixedCents ?? 0) / 100).toFixed(2)}/session`;
  }
  if (c.commissionMode === 'pct_of_net') {
    return `${Number(c.commissionPct ?? 0)}% of sales`;
  }
  return '—';
}

export default function AmbassadorDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();

  const isAmbassador =
    !!user && (user.role === 'ambassador' || user.isAmbassador === true || user.role === 'admin');

  // Handle Stripe Connect return (?connect=success|refresh)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get('connect');
    if (connectParam === 'success') {
      toast({
        title: 'Payout setup complete',
        description:
          'Your Stripe account is connected. Referral commissions will be transferred after the holding period.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/ambassador/connect/status'] });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (connectParam === 'refresh') {
      toast({
        title: 'Stripe onboarding session expired',
        description: 'Please click "Set up payouts" to restart your onboarding.',
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const { data: connectStatus } = useQuery<ConnectStatus>({
    queryKey: ['/api/ambassador/connect/status'],
    enabled: isAmbassador,
    queryFn: async () => authedJson<ConnectStatus>('/api/ambassador/connect/status'),
  });

  const { data: earnings, isLoading: earningsLoading } = useQuery<Earnings>({
    queryKey: ['/api/ambassador/earnings'],
    enabled: isAmbassador,
    queryFn: async () => authedJson<Earnings>('/api/ambassador/earnings'),
  });

  const { data: referralData, isLoading: referralsLoading } = useQuery<ReferralsResponse>({
    queryKey: ['/api/ambassador/referrals'],
    enabled: isAmbassador,
    queryFn: async () => authedJson<ReferralsResponse>('/api/ambassador/referrals'),
  });

  const connectOnboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/ambassador/connect/onboard', { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to start Stripe onboarding.');
      }
      return (await response.json()) as { url: string };
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to start Stripe onboarding. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const connectDashboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/ambassador/connect/dashboard', { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to open Stripe settings.');
      }
      return (await response.json()) as { url: string };
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Stripe settings unavailable',
        description:
          error instanceof Error ? error.message : 'Unable to open Stripe settings. Please try again.',
        variant: 'destructive',
      });
    },
  });

  if (!isAmbassador) {
    return (
      <PublicShell routeKey="ambassador-dashboard">
        <div className="page-shell py-8 text-center">
          <h1 className="text-2xl font-bold mb-2">Ambassador Dashboard</h1>
          <p className="text-muted-foreground">
            This page is for Kalovate ambassadors. If you refer creators to Kalovate, contact us to
            get set up.
          </p>
        </div>
      </PublicShell>
    );
  }

  const referrals = referralData?.referrals ?? [];
  const assignedClasses = referralData?.assignedClasses ?? [];

  return (
    <PublicShell routeKey="ambassador-dashboard">
      <div className="page-shell py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="ambassador-dashboard-title">
            Ambassador Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your referral commissions and manage your payout account.
          </p>
        </div>

        {/* Connect status */}
        {connectStatus && (
          <Card className={connectStatus.chargesEnabled ? 'border-green-300' : 'border-orange-300'}>
            <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <DollarSign
                  size={20}
                  className={
                    connectStatus.chargesEnabled
                      ? 'text-green-600 shrink-0'
                      : 'text-orange-600 shrink-0'
                  }
                />
                <div className="min-w-0">
                  <p className="font-medium text-sm">
                    {connectStatus.chargesEnabled
                      ? 'Payouts active'
                      : connectStatus.onboardingStatus === 'in_progress'
                        ? 'Payout setup in progress'
                        : 'Payout account not connected'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {connectStatus.chargesEnabled
                      ? 'Your commissions will be transferred after the holding period.'
                      : 'Complete Stripe onboarding to receive your referral commissions.'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                {!connectStatus.chargesEnabled && (
                  <Button
                    size="sm"
                    onClick={() => connectOnboardMutation.mutate()}
                    disabled={connectOnboardMutation.isPending}
                    data-testid="button-ambassador-connect-onboard"
                  >
                    {connectOnboardMutation.isPending
                      ? 'Redirecting…'
                      : connectStatus.onboardingStatus === 'in_progress'
                        ? 'Continue setup'
                        : 'Set up payouts'}
                  </Button>
                )}
                {connectStatus.connected && (
                  <Button
                    size="sm"
                    variant={connectStatus.chargesEnabled ? 'default' : 'outline'}
                    onClick={() => connectDashboardMutation.mutate()}
                    disabled={connectDashboardMutation.isPending}
                    data-testid="button-ambassador-connect-dashboard"
                  >
                    {connectDashboardMutation.isPending ? 'Opening…' : 'Edit Stripe settings'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Earnings summary */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Current Balance</CardDescription>
            </CardHeader>
            <CardContent>
              {earningsLoading ? (
                <div className="h-8 w-24 bg-muted animate-pulse rounded" />
              ) : (
                <p className="text-2xl font-bold" data-testid="ambassador-balance">
                  ${((earnings?.balanceCents ?? 0) / 100).toFixed(2)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">After payouts</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Lifetime Earned</CardDescription>
            </CardHeader>
            <CardContent>
              {earningsLoading ? (
                <div className="h-8 w-24 bg-muted animate-pulse rounded" />
              ) : (
                <p className="text-2xl font-bold" data-testid="ambassador-lifetime-earned">
                  ${((earnings?.lifetimeEarnedCents ?? 0) / 100).toFixed(2)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">Gross commissions</p>
            </CardContent>
          </Card>
        </div>

        {/* Referred creators */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={18} />
              Referred Creators
            </CardTitle>
            <CardDescription>Creators whose sales earn you a commission</CardDescription>
          </CardHeader>
          <CardContent>
            {referralsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : referrals.length > 0 ? (
              <div className="space-y-3">
                {referrals.map(r => (
                  <div
                    key={r.id}
                    className="flex flex-col gap-2 border rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="font-medium break-words">{r.creatorDisplayName ?? 'Creator'}</p>
                      <p className="text-sm text-muted-foreground">{formatCommission(r)}</p>
                    </div>
                    <Badge variant={r.status === 'active' ? 'default' : 'secondary'} className="shrink-0">
                      {r.status === 'active' ? 'Active' : 'Ended'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No referred creators yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  When Kalovate links a creator you referred, they'll appear here.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Directly assigned classes */}
        {assignedClasses.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen size={18} />
                Assigned Classes
              </CardTitle>
              <CardDescription>Classes crediting you directly</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {assignedClasses.map(c => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-3 border rounded-lg p-3"
                  >
                    <p className="font-medium min-w-0 break-words">{c.title}</p>
                    <Badge variant="outline" className="shrink-0">
                      {formatCommission(c)}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction history */}
        <Card>
          <CardHeader>
            <CardTitle>Commission History</CardTitle>
            <CardDescription>Most recent 50 ledger entries</CardDescription>
          </CardHeader>
          <CardContent>
            {earningsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : earnings && earnings.entries.length > 0 ? (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 pr-4 font-medium">Date</th>
                      <th className="text-left py-2 pr-4 font-medium">Type</th>
                      <th className="text-left py-2 pr-4 font-medium">Reference</th>
                      <th className="text-right py-2 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.entries.map(entry => {
                      const amountDollars = entry.amountCents / 100;
                      const isNegative = amountDollars < 0;
                      return (
                        <tr key={entry.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 text-muted-foreground">
                            {formatDateCompact(entry.createdAt)}
                          </td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline" className="capitalize text-xs">
                              {entry.entryType.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground truncate max-w-[200px]">
                            {entry.referenceNote ?? entry.orderId ?? '—'}
                          </td>
                          <td
                            className={`py-2 text-right font-medium tabular-nums ${isNegative ? 'text-destructive' : 'text-foreground'}`}
                          >
                            {isNegative ? '-' : '+'}${Math.abs(amountDollars).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No commissions yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your share of referred creators' sales will appear here.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicShell>
  );
}
